import {
  GroupedAssignedItems,
  OrderItems,
  RunForBatchDetail,
} from '../../../batchDetails/types';
import React, {useCallback} from 'react';
import {getClientType, getStoreName} from '@buncha/common/types/common';

import {Analytics} from '../../../config/analytics/analytics';
import BatchOrderItem from '../BatchOrderItem';
import {CustomFlatList} from '../../../components/core/list';
import {Divider} from 'native-base';
import {EventType} from '../../../config/analytics/type';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '../../../itemDetails/navigation/types';
import {ShopperScreens} from '../../../home/navigation/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {ToastType} from '@buncha/components/composites/notification/type';
import {navigation} from '../../../config/navigation';
import {styles} from './styles';
import {useCheckForFrozenItems} from '@buncha/batchDetails/hooks/AddItem';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useToastMessage} from '@buncha/components/composites/notification';

interface BatchOrderItemListProps {
  orderItems: OrderItems[];
  runDetail?: RunForBatchDetail;
  groupedAssignedItemIds: GroupedAssignedItems[];
  onClickHandler?: (orderItem: OrderItems) => Promise<void>;
}

function BatchOrderItemList(props: BatchOrderItemListProps) {
  const [showAlertToast] = useToastMessage(ToastType.Alert);

  const {checkForFrozenItems, checkingItemForPick} = useCheckForFrozenItems();
  const {orderItems, runDetail, groupedAssignedItemIds} = props;
  const isMeijerStore = useIsMeijerStore(runDetail?.store?.id);
  const navigateItemDetail = useCallback(
    (orderItem: OrderItems) => {
      Analytics.eventWithProps('Navigate to Item Detail', {
        type: EventType.cta,
        itemName: orderItem?.name,
        quanity: orderItem?.quantity,
        catalogItemId: orderItem.catalogItemId,
        orderItemId: orderItem.id,
        storeIndex: runDetail?.store?.elasticSearchIndex,
        runId: runDetail?.id,
        client: getClientType(isMeijerStore),
        storeType: getStoreName(String(runDetail?.store?.id)),
      });
      // if (!orderItem.isCustomItem)
      navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.ItemDetailsPage,
        params: {
          catalogItemId: orderItem.catalogItemId,
          orderItemId: orderItem.id,
          storeIndex: runDetail?.store?.elasticSearchIndex,
          runId: runDetail?.id,
          storeId: runDetail?.store?.id,
        },
      });
    },
    [
      isMeijerStore,
      runDetail?.id,
      runDetail?.store?.elasticSearchIndex,
      runDetail?.store?.id,
    ],
  );

  const getItemIds = useCallback(
    (groupedAssignedOrderItems: GroupedAssignedItems[]) => {
      return groupedAssignedOrderItems.flatMap(AssignedItem =>
        AssignedItem.orderItems.map(item => item.id),
      );
    },
    [],
  );

  const onClickHandler = useCallback(
    async (orderItem: OrderItems) => {
      if (orderItem.isAdjacentOOSItem)
        return navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.SubstituteItemDetailPage,
          params: {
            catalogItemId: orderItem.catalogItemId,
            orderItemId: orderItem.id,
            storeIndex: runDetail?.store?.elasticSearchIndex,
            runId: runDetail?.id,
            adjacentOOSId: orderItem.adjacentOOSItem?.id,
          },
        });

      switch (orderItem.tags === 'Frozen') {
        case true: {
          const allowToPick = await checkForFrozenItems(
            runDetail?.id,
            getItemIds(groupedAssignedItemIds),
          );
          if (!allowToPick) navigateItemDetail(orderItem);
          else
            showAlertToast(
              'Please finish picking non-frozen items to keep frozen order items as cold as possible.',
            );
          break;
        }
        case false:
          navigateItemDetail(orderItem);
          break;
        default:
          break;
      }
    },
    [
      checkForFrozenItems,
      getItemIds,
      groupedAssignedItemIds,
      navigateItemDetail,
      runDetail?.id,
      runDetail?.store?.elasticSearchIndex,
      showAlertToast,
    ],
  );

  const renderItem = useCallback(
    ({item}: {item: any; index: number}) => {
      const handler = props?.onClickHandler ?? onClickHandler;
      return (
        <BatchOrderItem
          runId={runDetail?.id}
          orderItem={item}
          onClick={async () => await handler(item)}
          esIndex={runDetail?.store?.elasticSearchIndex ?? ''}
          storeId={runDetail?.store?.id}
          enableGesture
        />
      );
    },
    [
      onClickHandler,
      props?.onClickHandler,
      runDetail?.id,
      runDetail?.store?.id,
      runDetail?.store?.elasticSearchIndex,
    ],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);

  return (
    <React.Fragment>
      <If condition={checkingItemForPick}>
        <SpinnerComponent size={'sm'} />
      </If>
      <CustomFlatList
        data={orderItems}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
        // ListFooterComponent={Separator}
      />
    </React.Fragment>
  );
}

export default React.memo(BatchOrderItemList);
